import {useMemo, useRef} from "react"
import {useDispatch, useSelector} from "react-redux"
import {
	Box,
	Card,
	IconButton,
	Stack, Tooltip,
	Typography,
	useTheme,
} from "@mui/material"
import {
	CheckBox, Close,
	DisabledByDefaultOutlined,
	//Download,
	FormatQuoteRounded,
	Reply
} from "@mui/icons-material"
import {openModal} from "../../reducers/copyCitationsModalSlice"
import {
	clearSelectedAndCloseMultiSelect,
	clearSelectedArticles, selectAllArticles,
	selectNumberOfSelectedArticles, selectOpenMultiSelect,
} from "../../reducers/selectedArticlesSlice"
import {selectDynamicView, DYNAMIC_PANEL_TAB_INDEX_MAP} from "../../reducers/dynamicViewSlice"
import {Transition} from "react-transition-group"
import "../../styles/MultiselectAnimations.css"
import {openShareArticlesModal} from "../../reducers/dashboardModalControlSlice"
import {useSnackbar} from "notistack"
import {useWorkspaceContext} from "../../pages/Dashboard"

const MULTI_SELECT_TOOLBAR_HEIGHT = "2rem"

export default function MultiSelect() {
	useTheme()
	const {workspaceId} = useWorkspaceContext()
	const dispatch = useDispatch()
	const numberOfSelectedSelector = useMemo(() => selectNumberOfSelectedArticles(workspaceId), [workspaceId])
	const articleCount = useSelector(numberOfSelectedSelector)
	const open = useSelector((state) => selectOpenMultiSelect(state, workspaceId))
	const dynamicView = useSelector((state) => selectDynamicView(state, workspaceId))
	const disableSelectAll = dynamicView === DYNAMIC_PANEL_TAB_INDEX_MAP.AUTOMATED_SEARCH || dynamicView === DYNAMIC_PANEL_TAB_INDEX_MAP.ARTICLE_SEARCH
	const multiselectRef = useRef(null)
	const noArticlesSelected = (articleCount === "-" || articleCount <= 0)

	return (

		<Transition
			nodeRef={multiselectRef}
			in={open}
			timeout={{
				enter: 0, // setting this value creates a delay BEFORE the transition runs?
				exit: 100, // but this is required to make the exit transition run... don't ask me why.
			}}
			mountOnEnter
			unmountOnExit
		>
			{transitionState => (
				<Card
					className={`multiselect-${transitionState}`}
					ref={multiselectRef}
					raised
					sx={{
						backgroundColor: "primary.main",
						color: "#FFF",
						maxWidth: "95%",
						display: "flex",
						marginBottom: "10px",
						zIndex: 100,
						transition: "opacity 300ms cubic-bezier(0, 0, 0.2, 1), transform 300ms cubic-bezier(0, 0, 0.2, 1)",
						position: "absolute",
						bottom: 0,
						alignSelf: "center",
						overflow: "clip",
						height: MULTI_SELECT_TOOLBAR_HEIGHT
					}}
				>
					<Stack
						direction={"row"}
						//spacing={"6px"}
						height={"4rem"}
						sx={{
							alignSelf: "center",
							justifyContent: "space-around",
							alignItems: "center",
						}}
					>
						<MultiSelectIcon name={"Cite"}
							MSIcon={FormatQuoteRounded}
							action={() => dispatch(openModal(workspaceId))}
							disable={noArticlesSelected}
							disableMessage={"Please select at least one article"}
						/>
						<MultiSelectIcon name={"Share"}
							MSIcon={Reply}
							action={() => dispatch(openShareArticlesModal())}
							disable={noArticlesSelected}
							disableMessage={"Please select at least one article"}
							flipIcon={true}
						/>
						<MultiSelectIcon name={"Select All"}
							MSIcon={CheckBox}
							action={() => dispatch(selectAllArticles(workspaceId))}
							disable={disableSelectAll}
						/>
						<MultiSelectIcon name={"Clear Selected"}
							MSIcon={DisabledByDefaultOutlined}
							action={() => dispatch(clearSelectedArticles(workspaceId))}
							disable={noArticlesSelected || disableSelectAll}
							disableMessage={"Please select at least one article"}
						/>
						<Box sx={{display: "flex", flexDirection: "row", height: "100%", maxHeight: MULTI_SELECT_TOOLBAR_HEIGHT}}>
							<SelectionCounter
								articleCount={articleCount}
							/>
							<HorizCloseButton
								closeAction={() => dispatch(clearSelectedAndCloseMultiSelect(workspaceId))}
							/>
						</Box>
					</Stack>
				</Card>
			)}
		</Transition>
	)
}


function MultiSelectIcon({
	name,
	MSIcon,
	action,
	flipIcon = false,
	highlightIcon = false,
	disable = false,
	disableMessage = "Something went wrong",
}) {
	const {enqueueSnackbar} = useSnackbar()
	const checkForDisabledAndHandleClick = () => {
		if (disable) {
			enqueueSnackbar(disableMessage, {variant: "error", autoHideDuration: 5000})
		} else {
			action()
		}
	}

	return (
		<Tooltip
			title={name}
			placement={"top"}
			arrow
			slotProps={{
				popper: {
					modifiers: [
						{
							name: "offset",
							options: {
								offset: [0, 0],
							},
						},
					],
				},
			}}
		>
			<IconButton
				sx={{
					display: "flex",
					flexDirection: "column",
					justifyContent: "center",
					maxHeight: "100%",
					width: "4.5rem",
					height: MULTI_SELECT_TOOLBAR_HEIGHT,
					padding: "0 8px",
					color: "#FFF",
					backgroundColor: highlightIcon ? "primary.mainROne" : "transparent",
					"&:hover": {
						backgroundColor: "primary.mainROne"
					},
					"&:not(:first-of-type)": {
						borderLeft: "1px solid rgba(255,255,255,0.50)"
					},
				}}
				onClick={checkForDisabledAndHandleClick}
			>
				<MSIcon fontSize={"2rem"} sx={{transform: flipIcon ? "scaleX(-1)" : "none"}}/>
				{/*<Typography variant={"caption"}>{name}</Typography>*/}
			</IconButton>
		</Tooltip>
	)
}

function SelectionCounter({articleCount}) {
	return (
		<Tooltip
			title={articleCount !== "-" || articleCount < 1 ? `${articleCount} Citations Selected` : "No Citations Selected"}
			placement={"top"}
			arrow
			slotProps={{
				popper: {
					modifiers: [
						{
							name: "offset",
							options: {
								offset: [0, 0],
							},
						},
					],
				},
			}}
		>
			<Box
				sx={{
					justifySelf: "start",
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					width: "4.5rem",
					height: MULTI_SELECT_TOOLBAR_HEIGHT,
					backgroundColor: "primary.main",
					flexDirection: "column",
					//height: "100%",
					flex: "1 0 auto",
					borderLeft: "1px solid rgba(255,255,255,0.50)",
					cursor: "default",
				}}
			>
				<Typography
					variant={"h3"}
					sx={{
						margin: "0 5px",
						lineHeight: "0.9",
						fontWeight: 600,
						fontSize: "2rem"
					}}
				>
					{" "}
					{articleCount}{" "}
				</Typography>
				{/*<Typography variant={"caption"}>selected</Typography>*/}
			</Box>
		</Tooltip>
	)
}

function HorizCloseButton({closeAction}) {
	return (
		<Tooltip
			title={"Close"}
			placement={"top"}
			arrow
			slotProps={{
				popper: {
					modifiers: [
						{
							name: "offset",
							options: {
								offset: [0, 0],
							},
						},
					],
				},
			}}
		>
			<Box
				onClick={closeAction}
				sx={{
					display: "flex",
					flexDirection: "column",
					justifyContent: "center",
					alignItems: "center",
					height: MULTI_SELECT_TOOLBAR_HEIGHT,
					width: "1.5rem",
					backgroundColor: "alertRed.ROne",
					cursor: "pointer",
					"&:hover": {
						backgroundColor: "alertRed.highlightTransp70",
					},
					borderTopRightRadius: "4px",
					borderBottomRightRadius: "4px",
				}}
			>
				<Close fontSize={"inherit"}/>
			</Box>
		</Tooltip>
	)
}
